import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  HostListener,
  OnDestroy,
  effect,
  inject,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RoleService } from '@app/shared/services/roles.service';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { EzGridComponent } from '@template-ui/components/ez-grid/ez-grid.component';
import { ButtonsCellRenderer } from '@template-ui/components/ez-grid/cell-renderers/buttons-cell-renderer/buttons-cell-renderer.component';
import { EzStandardStateComponent } from '@template-ui/components/ez-standard-state/ez-standard-state.component';
import type { ColDef, GridOptions } from 'ag-grid-community';
import { firstValueFrom } from 'rxjs';
import { DocumentAssetsStateService } from './document-assets-state.service';
import { DocumentAssetsService } from './document-assets.service';
import {
  sameAssetReference,
  type DocumentAssetCatalog,
  type DocumentAssetScope,
  type DocumentAssetVersion,
  type DocumentBrandStyle,
  type DocumentBrandVersion,
  type DocumentAssetManifest,
} from './document-assets.models';

@Component({
  selector: 'phms-platform-document-assets',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    UiButtonDirective,
    UiControlDirective,
    EzGridComponent,
    EzStandardStateComponent,
  ],
  templateUrl: './platform-document-assets.component.html',
  styleUrl: './platform-document-assets.component.scss',
})
export class PlatformDocumentAssetsComponent implements OnDestroy {
  private readonly api = inject(DocumentAssetsService);
  private readonly roles = inject(RoleService);
  readonly state = inject(DocumentAssetsStateService);
  readonly canRead = this.roles.hasAnyPermissionSignal('content_template.read');
  catalog: DocumentAssetCatalog | null = null;
  scopeKey = '';
  loading = false;
  accessLost = false;
  error = '';
  notice = '';
  search = '';
  editor: 'asset' | 'brand' | null = null;
  replacingAsset: DocumentAssetVersion | null = null;
  replacingBrand: DocumentBrandVersion | null = null;
  name = '';
  alternativeText = '';
  file: File | null = null;
  logoKey = '';
  style: DocumentBrandStyle = this.emptyStyle();
  private destroyed = false;
  private generation = 0;
  private actor = '';
  private requestIntent: { fingerprint: string; requestId: string } | null =
    null;

  readonly assetColumns: ColDef<DocumentAssetVersion>[] = [
    { field: 'name', headerName: 'Asset', minWidth: 150, flex: 1 },
    {
      headerName: 'Version',
      minWidth: 100,
      valueGetter: ({ data }) =>
        data
          ? `${data.reference.version}${data.isLatest ? ' · Latest' : ''}`
          : '',
    },
    {
      field: 'alternativeText',
      headerName: 'Alternative text',
      minWidth: 180,
      flex: 1,
    },
    { field: 'contentType', headerName: 'Type', minWidth: 110 },
    { field: 'usageCount', headerName: 'Retained uses', minWidth: 100 },
    {
      headerName: '',
      minWidth: 115,
      cellRenderer: ButtonsCellRenderer,
      cellRendererParams: {
        buttonsConfig: [
          {
            id: 'version',
            buttonText: 'New version',
            disabled: (button?: {
              extraData?: {
                rowData?: DocumentAssetVersion | DocumentBrandVersion;
              };
            }) =>
              !this.canUpload ||
              !button?.extraData?.rowData?.isLatest ||
              this.key(button!.extraData!.rowData!) !== this.scopeKey,
          },
        ],
        onButtonClick: ({ rowData }: { rowData: DocumentAssetVersion }) =>
          void this.beginAsset(rowData),
      },
    },
  ];
  readonly brandColumns: ColDef<DocumentBrandVersion>[] = [
    { field: 'name', headerName: 'Brand profile', minWidth: 160, flex: 1 },
    {
      headerName: 'Version',
      minWidth: 100,
      valueGetter: ({ data }) =>
        data
          ? `${data.reference.version}${data.isLatest ? ' · Latest' : ''}`
          : '',
    },
    { field: 'scopeTypeCode', headerName: 'Owner scope', minWidth: 120 },
    { field: 'usageCount', headerName: 'Retained uses', minWidth: 100 },
    {
      headerName: '',
      minWidth: 115,
      cellRenderer: ButtonsCellRenderer,
      cellRendererParams: {
        buttonsConfig: [
          {
            id: 'version',
            buttonText: 'New version',
            disabled: (button?: {
              extraData?: {
                rowData?: DocumentAssetVersion | DocumentBrandVersion;
              };
            }) =>
              !this.canUpload ||
              !button?.extraData?.rowData?.isLatest ||
              this.key(button!.extraData!.rowData!) !== this.scopeKey,
          },
        ],
        onButtonClick: ({ rowData }: { rowData: DocumentBrandVersion }) =>
          void this.beginBrand(rowData),
      },
    },
    {
      headerName: '',
      minWidth: 90,
      cellRenderer: ButtonsCellRenderer,
      cellRendererParams: {
        buttonsConfig: [{ id: 'export', buttonText: 'Export' }],
        onButtonClick: ({ rowData }: { rowData: DocumentBrandVersion }) =>
          void this.exportBrand(rowData),
      },
    },
  ];
  readonly gridOptions: GridOptions = {
    domLayout: 'autoHeight',
    getRowId: ({ data }) => JSON.stringify(data.reference),
  };

  constructor() {
    effect(() => {
      const allowed = this.canRead();
      const actor = this.roles.accessProfile()?.actor.actorId ?? '';
      untracked(() => {
        if (!allowed || !actor) {
          this.clear();
          return;
        }
        if (this.actor && this.actor !== actor) this.clear();
        this.actor = actor;
        void this.load();
      });
    });
  }
  get scope(): DocumentAssetScope | undefined {
    return this.catalog?.scopes.find(
      (scope) => this.key(scope) === this.scopeKey,
    );
  }
  key(scope: Pick<DocumentAssetScope, 'scopeTypeCode' | 'scopeKey'>): string {
    return JSON.stringify([scope.scopeTypeCode, scope.scopeKey]);
  }
  get busy(): boolean {
    return this.state.busy();
  }
  get canUpload(): boolean {
    return (
      !!this.scope &&
      !!this.catalog?.canUpload &&
      !this.busy &&
      !this.loading &&
      !this.accessLost &&
      this.canRead() &&
      this.roles.accessProfile()?.actor.actorId === this.actor
    );
  }
  private readonly emptyAssets: DocumentAssetVersion[] = [];
  private readonly emptyBrands: DocumentBrandVersion[] = [];
  private filteredAssets: {
    items: DocumentAssetVersion[];
    term: string;
    rows: DocumentAssetVersion[];
  } | null = null;
  private filteredBrands: {
    items: DocumentBrandVersion[];
    term: string;
    rows: DocumentBrandVersion[];
  } | null = null;
  get assetRows(): DocumentAssetVersion[] {
    const items = this.catalog?.items ?? this.emptyAssets,
      term = this.search.trim().toLowerCase();
    if (!term) return items;
    if (
      this.filteredAssets?.items === items &&
      this.filteredAssets.term === term
    )
      return this.filteredAssets.rows;
    const rows = items.filter((item) =>
      `${item.name} ${item.alternativeText}`.toLowerCase().includes(term),
    );
    this.filteredAssets = { items, term, rows };
    return rows;
  }
  get brandRows(): DocumentBrandVersion[] {
    const items = this.catalog?.brands ?? this.emptyBrands,
      term = this.search.trim().toLowerCase();
    if (!term) return items;
    if (
      this.filteredBrands?.items === items &&
      this.filteredBrands.term === term
    )
      return this.filteredBrands.rows;
    const rows = items.filter((item) => item.name.toLowerCase().includes(term));
    this.filteredBrands = { items, term, rows };
    return rows;
  }
  get canSave(): boolean {
    return (
      this.canUpload &&
      !!this.name.trim() &&
      (this.editor === 'brand' ||
        (this.editor === 'asset' &&
          !!this.file &&
          !!this.alternativeText.trim()))
    );
  }
  private emptyStyle(): DocumentBrandStyle {
    return {
      fontCode: 'lato-v1',
      fontSha256: '',
      logo: null,
      logoWidthPt: 54,
      logoHeightPt: 54,
      logoAlignment: 'start',
      accentColor: '#183B56',
      watermarkText: '',
      watermarkColor: '#737373',
      watermarkOpacity: 0.15,
      watermarkRotation: -35,
    };
  }
  changed(): void {
    this.state.setDirty(this.editor !== null);
    this.requestIntent = null;
    this.error = '';
  }
  async load(): Promise<void> {
    if (this.destroyed || !this.canRead() || this.busy) return;
    const generation = ++this.generation;
    this.loading = true;
    this.error = '';
    try {
      let catalog = await firstValueFrom(this.api.catalog(this.scope));
      if (!this.current(generation)) return;
      if (!this.scopeKey && catalog.scopes.length) {
        this.scopeKey = this.key(catalog.scopes[0]);
        catalog = await firstValueFrom(this.api.catalog(catalog.scopes[0]));
        if (!this.current(generation)) return;
      }
      this.catalog = catalog;
      this.accessLost = false;
    } catch (error) {
      if (this.current(generation)) this.fail(error);
    } finally {
      if (generation === this.generation) this.loading = false;
    }
  }
  checkAccess(): void {
    if (this.busy || this.destroyed) return;
    this.roles.reload();
    if (this.canRead()) void this.load();
  }
  async changeScope(value: string): Promise<void> {
    if (this.busy || value === this.scopeKey) return;
    const generation = this.generation;
    if (!(await this.state.confirmNavigation()) || !this.current(generation))
      return;
    this.cancel();
    this.scopeKey = value;
    await this.load();
  }
  async beginAsset(item: DocumentAssetVersion | null = null): Promise<void> {
    if (
      !this.canUpload ||
      (item && (!item.isLatest || this.key(item) !== this.scopeKey))
    )
      return;
    const generation = this.generation;
    if (!(await this.state.confirmNavigation()) || !this.current(generation))
      return;
    this.cancel();
    this.editor = 'asset';
    this.replacingAsset = item;
    this.name = item?.name ?? '';
    this.alternativeText = item?.alternativeText ?? '';
  }
  async beginBrand(item: DocumentBrandVersion | null = null): Promise<void> {
    if (
      !this.canUpload ||
      (item && (!item.isLatest || this.key(item) !== this.scopeKey))
    )
      return;
    const generation = this.generation;
    if (!(await this.state.confirmNavigation()) || !this.current(generation))
      return;
    this.cancel();
    this.editor = 'brand';
    this.replacingBrand = item;
    this.name = item?.name ?? '';
    this.style = item ? structuredClone(item.style) : this.emptyStyle();
    this.style.fontSha256 =
      this.catalog?.fonts.find(
        (font) => font.code === this.style.fontCode && font.available,
      )?.sha256 ?? '';
    this.logoKey = this.style.logo ? JSON.stringify(this.style.logo) : '';
  }
  cancel(): void {
    if (this.busy) return;
    this.editor = null;
    this.replacingAsset = null;
    this.replacingBrand = null;
    this.name = '';
    this.alternativeText = '';
    this.file = null;
    this.logoKey = '';
    this.requestIntent = null;
    this.state.setDirty(false);
  }
  selectFile(event: Event): void {
    if (!this.canUpload) return;
    const file = (event.target as HTMLInputElement).files?.item(0) ?? null;
    this.file = null;
    this.changed();
    if (!file) return;
    if (
      !['image/png', 'image/jpeg'].includes(file.type) ||
      !/\.(png|jpe?g)$/i.test(file.name) ||
      file.size <= 0 ||
      file.size > (this.catalog?.limits.maxBytes ?? 2097152)
    ) {
      this.error =
        'Choose a nonempty PNG or JPEG within the registered size limit. SVG, remote URLs and executable content are unavailable.';
      return;
    }
    this.file = file;
  }
  chooseLogo(value: string): void {
    if (!this.canUpload) return;
    const logo = this.catalog?.items.find(
      (item) => JSON.stringify(item.reference) === value,
    );
    this.logoKey = logo ? JSON.stringify(logo.reference) : '';
    this.style.logo = logo ? structuredClone(logo.reference) : null;
    this.changed();
  }
  async save(): Promise<void> {
    const scope = this.scope;
    if (!scope || !this.canSave || !this.catalog) return;
    const generation = this.generation,
      mode = this.editor;
    this.state.setBusy(true);
    this.error = '';
    this.notice = '';
    try {
      if (mode === 'asset') {
        const file = this.file!;
        const buffer = await file.arrayBuffer();
        if (!this.current(generation)) return;
        const bytes = new Uint8Array(buffer);
        if (!bytes.length || bytes.length > this.catalog.limits.maxBytes)
          throw new Error(
            'The selected file exceeds the registered size limit.',
          );
        let binary = '';
        for (const byte of bytes) binary += String.fromCharCode(byte);
        const payload = {
          assetId: this.replacingAsset?.reference.assetId ?? null,
          expectedVersion: this.replacingAsset?.reference.version ?? null,
          scopeTypeCode: scope.scopeTypeCode,
          scopeKey: scope.scopeKey,
          name: this.name.trim(),
          alternativeText: this.alternativeText.trim(),
          fileName: file.name,
          contentType: file.type,
          bytesBase64: btoa(binary),
        };
        const requestId = this.intent(payload);
        const result = await firstValueFrom(
          this.api.upload({ requestId, ...payload }),
        );
        if (!this.current(generation)) return;
        this.notice = `${result.name} version ${result.reference.version} uploaded and verified. Existing versions are retained.`;
      } else if (mode === 'brand') {
        const payload = {
          brandId: this.replacingBrand?.reference.brandId ?? null,
          expectedVersion: this.replacingBrand?.reference.version ?? null,
          scopeTypeCode: scope.scopeTypeCode,
          scopeKey: scope.scopeKey,
          name: this.name.trim(),
          style: structuredClone(this.style),
        };
        const result = await firstValueFrom(
          this.api.saveBrand({ requestId: this.intent(payload), ...payload }),
        );
        if (!this.current(generation)) return;
        this.notice = `${result.name} version ${result.reference.version} saved. Select this exact profile version in document settings.`;
      }
      this.state.setBusy(false);
      this.cancel();
      await this.load();
    } catch (error) {
      if (this.current(generation)) this.fail(error);
    } finally {
      if (generation === this.generation) this.state.setBusy(false);
    }
  }
  async exportBrand(brand: DocumentBrandVersion): Promise<void> {
    if (this.busy || !this.canRead() || this.accessLost) return;
    const generation = this.generation;
    this.state.setBusy(true);
    try {
      const manifest = await firstValueFrom(
        this.api.exportBrand(brand.reference),
      );
      if (!this.current(generation)) return;
      const url = URL.createObjectURL(
        new Blob([JSON.stringify(manifest, null, 2)], {
          type: 'application/json',
        }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = `document-brand-${brand.reference.brandId}-v${brand.reference.version}.json`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 0);
      this.notice =
        'Exported the exact asset-side manifest. Whole-pack promotion remains a separate workflow.';
    } catch (error) {
      if (this.current(generation)) this.fail(error);
    } finally {
      if (generation === this.generation) this.state.setBusy(false);
    }
  }
  async importFile(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement,
      file = input.files?.item(0),
      scope = this.scope;
    input.value = '';
    if (!file || !scope || !this.canUpload) return;
    if (file.size > 4 * 1024 * 1024) {
      this.error = 'Choose an asset manifest under 4 MiB.';
      return;
    }
    const generation = this.generation;
    if (!(await this.state.confirmNavigation()) || !this.current(generation))
      return;
    this.state.setBusy(true);
    this.error = '';
    try {
      const manifest = JSON.parse(await file.text()) as DocumentAssetManifest;
      if (!this.current(generation)) return;
      const result = await firstValueFrom(
        this.api.importManifest(
          this.intent({ scope, manifest }),
          scope,
          manifest,
        ),
      );
      if (!this.current(generation)) return;
      this.notice = `Imported ${result.name} version ${result.reference.version} after compatibility checks.`;
      this.state.setBusy(false);
      this.cancel();
      await this.load();
    } catch (error) {
      if (this.current(generation)) this.fail(error);
    } finally {
      if (generation === this.generation) this.state.setBusy(false);
    }
  }
  referenceKey(item: DocumentAssetVersion): string {
    return JSON.stringify(item.reference);
  }
  selectedLogo(): DocumentAssetVersion | undefined {
    return this.catalog?.items.find((item) =>
      sameAssetReference(item.reference, this.style.logo),
    );
  }
  private intent(payload: unknown): string {
    const fingerprint = JSON.stringify(payload);
    if (this.requestIntent?.fingerprint !== fingerprint)
      this.requestIntent = { fingerprint, requestId: crypto.randomUUID() };
    return this.requestIntent!.requestId;
  }
  private current(generation: number): boolean {
    return (
      !this.destroyed &&
      generation === this.generation &&
      this.canRead() &&
      this.roles.accessProfile()?.actor.actorId === this.actor
    );
  }
  private fail(error: unknown): void {
    if (
      error instanceof HttpErrorResponse &&
      [401, 403].includes(error.status)
    ) {
      this.clear();
      this.accessLost = true;
      this.error =
        'Document asset access changed. Refresh access before continuing.';
      return;
    }
    this.error =
      error instanceof HttpErrorResponse
        ? typeof error.error?.detail === 'string'
          ? error.error.detail
          : error.status === 409
            ? 'This version changed. Refresh the list before creating another version.'
            : 'The asset operation failed. Your fields are preserved; retry the same request or inspect the list before changing the request.'
        : error instanceof Error
          ? error.message
          : 'The document asset operation failed.';
  }
  private clear(): void {
    this.generation++;
    this.catalog = null;
    this.filteredAssets = null;
    this.filteredBrands = null;
    this.scopeKey = '';
    this.editor = null;
    this.file = null;
    this.name = '';
    this.alternativeText = '';
    this.replacingAsset = null;
    this.replacingBrand = null;
    this.style = this.emptyStyle();
    this.logoKey = '';
    this.notice = '';
    this.error = '';
    this.requestIntent = null;
    this.loading = false;
    this.state.clear();
  }
  @HostListener('window:beforeunload', ['$event']) protect(
    event: BeforeUnloadEvent,
  ): void {
    if (this.state.isNavigationBlocked()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }
  ngOnDestroy(): void {
    this.destroyed = true;
    this.clear();
  }
}
